import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Sprint 6.9.1 — Consolidé paiements formateurs B2B
// Table créée par migration 101 (paiements_formateurs).
// Pas dans database.types.ts (gen automatique pas encore exécuté) → typage local.

export type PaiementFormateurStatut = 'a_payer' | 'paye' | 'non_retribue';
export type DossierPaiementMode = 'virement' | 'cheque' | 'especes' | 'ccp' | 'carte';

export interface PaiementFormateurRow {
  id: string;
  entreprise_id: string;
  activite_id: string | null;
  annee: number;
  periode: string | null;
  formateur_tier_id: string | null;
  formateur_nom: string;
  formateur_coordonnees: string | null;
  formation_id: string | null;
  formation_titre: string | null;
  date_deroulement: string | null;
  montant: number;
  statut: PaiementFormateurStatut;
  date_reglement: string | null;
  mode_paiement: DossierPaiementMode | null;
  reference_operation: string | null;
  preuve_paiement_url: string | null;
  commercial_user_id: string | null;
  remarques: string | null;
  operation_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface PaiementsFormateursFilter {
  entrepriseId: string | null;
  annee?: number | null;
  statut?: PaiementFormateurStatut | null;
  formateurTierId?: string | null;
  search?: string;
}

export function usePaiementsFormateurs(filter: PaiementsFormateursFilter) {
  return useQuery({
    queryKey: ['paiements_formateurs', filter],
    enabled: !!filter.entrepriseId,
    queryFn: async (): Promise<PaiementFormateurRow[]> => {
      let q = supabase
        .from('paiements_formateurs')
        .select('*')
        .order('annee', { ascending: false })
        .order('date_deroulement', { ascending: false, nullsFirst: false })
        .limit(2000);
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.annee) q = q.eq('annee', filter.annee);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.formateurTierId) q = q.eq('formateur_tier_id', filter.formateurTierId);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(
          `formateur_nom.ilike.%${s}%,formation_titre.ilike.%${s}%,reference_operation.ilike.%${s}%,remarques.ilike.%${s}%`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as PaiementFormateurRow[];
    },
  });
}

// =====================================================================
// IMPORT — cascade auto tiers + formations + paiements
// =====================================================================

const STATUT_FROM_EXCEL: Record<string, PaiementFormateurStatut> = {
  paye: 'paye',
  payé: 'paye',
  payee: 'paye',
  payée: 'paye',
  non_paye: 'a_payer',
  'non paye': 'a_payer',
  'non payé': 'a_payer',
  non_payé: 'a_payer',
  non_retribue: 'non_retribue',
  'non retribue': 'non_retribue',
  'non rétribué': 'non_retribue',
  a_payer: 'a_payer',
};

const MODE_FROM_EXCEL: Record<string, DossierPaiementMode> = {
  virement: 'virement',
  cheque: 'cheque',
  chèque: 'cheque',
  especes: 'especes',
  espèces: 'especes',
  cash: 'especes',
  ccp: 'ccp',
  carte: 'carte',
};

export interface ImportFormateurRow {
  annee: string | number | null;
  periode: string | null;
  formateur: string | null;
  coordonnees: string | null;
  formation: string | null;
  date_deroulement: string | null;
  montant: string | number | null;
  statut: string | null;
  date_reglement: string | null;
  mode_paiement: string | null;
  reference_operation: string | null;
  preuve_paiement_url: string | null;
  commercial: string | null;
  remarques: string | null;
}

export interface ImportPaiementsFormateursInput {
  entrepriseId: string;
  activiteId: string | null;
  rows: ImportFormateurRow[];
}

export interface ImportPaiementsFormateursResult {
  paiementsCrees: number;
  formateursCrees: number;
  formationsCreees: number;
  errors: string[];
}

function normalizeKey(s: string | null | undefined): string {
  return (s ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseStatut(raw: string | null): PaiementFormateurStatut {
  const k = normalizeKey(raw);
  return STATUT_FROM_EXCEL[k] ?? 'a_payer';
}

function parseMode(raw: string | null): DossierPaiementMode | null {
  const k = normalizeKey(raw);
  return MODE_FROM_EXCEL[k] ?? null;
}

function parseDate(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.toString().trim();
  if (!s) return null;
  const m1 = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m1) return `${m1[1]}-${m1[2]}-${m1[3]}`;
  const m2 = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/.exec(s);
  if (m2) {
    const [, d, mo, yRaw] = m2;
    const y = (yRaw ?? '').length === 2 ? `20${yRaw}` : (yRaw ?? '');
    return `${y}-${(mo ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

function parseNumber(raw: string | number | null): number {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') return raw;
  // Sprint 6.9.1.2 — DZD : pas de centimes en pratique. On retire tout sauf chiffres et signe
  // pour éviter l'ambiguïté "20,000" (US milliers) vs "20,000" (FR=20.000 décimal). Excel KOST
  // arrive typiquement en "20 000" (NBSP), "20,000" (US), "20.000" (FR), "20'000" (CH).
  const cleaned = raw.toString().replace(/[^\d-]/g, '');
  if (cleaned === '' || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

export function useImportPaiementsFormateurs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: ImportPaiementsFormateursInput,
    ): Promise<ImportPaiementsFormateursResult> => {
      const result: ImportPaiementsFormateursResult = {
        paiementsCrees: 0,
        formateursCrees: 0,
        formationsCreees: 0,
        errors: [],
      };

      const { data: existingTiers = [] } = await supabase
        .from('tiers')
        .select('id, raison_sociale, telephone, email')
        .eq('entreprise_id', input.entrepriseId)
        .eq('type', 'formateur');
      const tierByKey = new Map<string, string>();
      for (const t of existingTiers as { id: string; raison_sociale: string }[]) {
        tierByKey.set(normalizeKey(t.raison_sociale), t.id);
      }

      const { data: existingFormations = [] } = await supabase
        .from('formations')
        .select('id, titre, code')
        .eq('entreprise_id', input.entrepriseId);
      const formationByKey = new Map<string, string>();
      for (const f of existingFormations as { id: string; titre: string }[]) {
        formationByKey.set(normalizeKey(f.titre), f.id);
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      for (let i = 0; i < input.rows.length; i++) {
        const row = input.rows[i];
        if (!row) continue;
        const lineNo = i + 2;
        try {
          const formateurNom = (row.formateur ?? '').toString().trim();
          if (!formateurNom) {
            result.errors.push(`L${lineNo}: nom formateur manquant`);
            continue;
          }
          const annee = parseNumber(row.annee) || new Date().getFullYear();
          const montant = parseNumber(row.montant);
          if (montant < 0) {
            result.errors.push(`L${lineNo}: montant négatif (${montant})`);
            continue;
          }

          // 1. Tier formateur — find or create
          const tierKey = normalizeKey(formateurNom);
          let formateurTierId = tierByKey.get(tierKey) ?? null;
          if (!formateurTierId) {
            const { data: created, error: errTier } = await supabase
              .from('tiers')
              .insert({
                entreprise_id: input.entrepriseId,
                type: 'formateur',
                raison_sociale: formateurNom,
                rib: row.coordonnees ?? null,
                activite_principale_id: input.activiteId,
              } as never)
              .select('id')
              .single();
            if (errTier) {
              result.errors.push(`L${lineNo}: tier formateur KO — ${errTier.message}`);
              continue;
            }
            formateurTierId = (created as { id: string } | null)?.id ?? null;
            if (formateurTierId) {
              tierByKey.set(tierKey, formateurTierId);
              result.formateursCrees++;
            }
          }

          // 2. Formation — find or create (si nom fourni)
          let formationId: string | null = null;
          const formationTitre = (row.formation ?? '').toString().trim();
          if (formationTitre) {
            const formKey = normalizeKey(formationTitre);
            formationId = formationByKey.get(formKey) ?? null;
            if (!formationId) {
              const code = `IMP-${Math.floor(Math.random() * 9000 + 1000)}`;
              const { data: createdF, error: errF } = await supabase
                .from('formations')
                .insert({
                  entreprise_id: input.entrepriseId,
                  code,
                  titre: formationTitre,
                } as never)
                .select('id')
                .single();
              if (errF) {
                result.errors.push(`L${lineNo}: formation KO — ${errF.message}`);
              } else {
                formationId = (createdF as { id: string } | null)?.id ?? null;
                if (formationId) {
                  formationByKey.set(formKey, formationId);
                  result.formationsCreees++;
                }
              }
            }
          }

          // 3. INSERT paiement_formateur
          const statut = parseStatut(row.statut);
          const { error: errIns } = await supabase.from('paiements_formateurs').insert({
            entreprise_id: input.entrepriseId,
            activite_id: input.activiteId,
            annee,
            periode: row.periode ?? null,
            formateur_tier_id: formateurTierId,
            formateur_nom: formateurNom,
            formateur_coordonnees: row.coordonnees ?? null,
            formation_id: formationId,
            formation_titre: formationTitre || null,
            date_deroulement: parseDate(row.date_deroulement),
            montant,
            statut,
            date_reglement: statut === 'paye' ? parseDate(row.date_reglement) : null,
            mode_paiement: statut === 'paye' ? parseMode(row.mode_paiement) : null,
            reference_operation: row.reference_operation ?? null,
            preuve_paiement_url: row.preuve_paiement_url ?? null,
            remarques: row.remarques ?? null,
            created_by: createdBy,
          } as never);
          if (errIns) {
            result.errors.push(`L${lineNo}: insert paiement KO — ${errIns.message}`);
            continue;
          }
          result.paiementsCrees++;
        } catch (e) {
          result.errors.push(`L${lineNo}: ${(e as Error).message}`);
        }
      }

      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['paiements_formateurs'] });
      void qc.invalidateQueries({ queryKey: ['tiers'] });
      void qc.invalidateQueries({ queryKey: ['formations'] });
    },
  });
}

// =====================================================================
// MARQUER PAYÉ — cross-ref opération SORTIE Caisse
// =====================================================================

export interface MarquerPayeInput {
  paiement: PaiementFormateurRow;
  dateReglement: string;
  modePaiement: DossierPaiementMode;
  reference: string | null;
  // Cible : exactement 1 (caisse_id si especes, compte_bancaire_id sinon)
  caisseId: string | null;
  compteBancaireId: string | null;
}

export function useMarquerPayeFormateur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarquerPayeInput): Promise<{ operation_id: string }> => {
      const { paiement, dateReglement, modePaiement, reference, caisseId, compteBancaireId } =
        input;

      // Canal cohérent avec target (contrainte operations_canal_target)
      let canal: 'especes' | 'ccp' | 'banque';
      if (modePaiement === 'especes') canal = 'especes';
      else if (modePaiement === 'ccp') canal = 'ccp';
      else canal = 'banque';

      if (canal === 'especes' && !caisseId) {
        throw new Error('Caisse obligatoire pour un paiement en espèces');
      }
      if (canal !== 'especes' && !compteBancaireId) {
        throw new Error('Compte bancaire obligatoire pour ce mode de paiement');
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const reference_op =
        reference ?? `FORM-${paiement.annee}-${Math.floor(Math.random() * 9000 + 1000)}`;
      const motif = paiement.formation_titre
        ? `Honoraires formateur — ${paiement.formation_titre}`
        : `Honoraires formateur — ${paiement.formateur_nom}`;

      const { data: opCreated, error: errOp } = await supabase
        .from('operations')
        .insert({
          entreprise_id: paiement.entreprise_id,
          reference: reference_op,
          date_operation: `${dateReglement}T12:00:00`,
          sens: 'sortie',
          canal,
          categorie: 'sous_traitance',
          caisse_id: canal === 'especes' ? caisseId : null,
          compte_bancaire_id: canal !== 'especes' ? compteBancaireId : null,
          tier_id: paiement.formateur_tier_id,
          activite_id: paiement.activite_id,
          activite_beneficiaire_id: paiement.activite_id,
          activite_emettrice_id: paiement.activite_id,
          devise_code: 'DZD',
          montant: paiement.montant,
          taux_change: 1,
          montant_dzd: paiement.montant,
          motif,
          observation: `Paiement formateur consolidé · réf interne ${paiement.id.slice(0, 8)}`,
          statut: 'validee',
          created_by: user?.id ?? null,
        } as never)
        .select('id')
        .single();
      if (errOp) throw new Error(`Opération KO : ${errOp.message}`);
      const operationId = (opCreated as { id: string } | null)?.id;
      if (!operationId) throw new Error('Opération créée sans id');

      const { error: errUpd } = await supabase
        .from('paiements_formateurs')
        .update({
          statut: 'paye',
          date_reglement: dateReglement,
          mode_paiement: modePaiement,
          reference_operation: reference_op,
          operation_id: operationId,
        } as never)
        .eq('id', paiement.id);
      if (errUpd) throw new Error(`Update paiement KO : ${errUpd.message}`);

      return { operation_id: operationId };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['paiements_formateurs'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

// =====================================================================
// CRÉATION MANUELLE (modal "+ Nouveau paiement")
// =====================================================================

export interface CreatePaiementFormateurInput {
  entreprise_id: string;
  activite_id: string | null;
  annee: number;
  periode: string | null;
  formateur_tier_id: string | null;
  formateur_nom: string;
  formateur_coordonnees: string | null;
  formation_id: string | null;
  formation_titre: string | null;
  date_deroulement: string | null;
  montant: number;
  statut: PaiementFormateurStatut;
  remarques: string | null;
}

export function useCreatePaiementFormateur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePaiementFormateurInput): Promise<PaiementFormateurRow> => {
      const { data, error } = await supabase
        .from('paiements_formateurs')
        .insert(input as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as PaiementFormateurRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['paiements_formateurs'] });
    },
  });
}

export function useDeletePaiementFormateur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paiement: { id: string; operation_id: string | null }): Promise<void> => {
      // Sprint 6.9.1.2 — cascade : si une opération Caisse est liée, on la supprime AVANT
      // (ON DELETE SET NULL côté FK donc paiement.operation_id deviendrait null sinon).
      if (paiement.operation_id) {
        const { error: errOp } = await supabase
          .from('operations')
          .delete()
          .eq('id', paiement.operation_id);
        if (errOp && !/no rows/i.test(errOp.message)) {
          throw new Error(`Suppression opération Caisse KO : ${errOp.message}`);
        }
      }
      const { error } = await supabase.from('paiements_formateurs').delete().eq('id', paiement.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['paiements_formateurs'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

// =====================================================================
// UPDATE — édition manuelle (drawer "Modifier")
// =====================================================================

export interface UpdatePaiementFormateurInput {
  id: string;
  annee?: number;
  periode?: string | null;
  formateur_tier_id?: string | null;
  formateur_nom?: string;
  formateur_coordonnees?: string | null;
  formation_id?: string | null;
  formation_titre?: string | null;
  date_deroulement?: string | null;
  montant?: number;
  statut?: PaiementFormateurStatut;
  date_reglement?: string | null;
  mode_paiement?: DossierPaiementMode | null;
  reference_operation?: string | null;
  preuve_paiement_url?: string | null;
  remarques?: string | null;
}

export function useUpdatePaiementFormateur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdatePaiementFormateurInput): Promise<void> => {
      const { id, ...rest } = input;
      const { error } = await supabase
        .from('paiements_formateurs')
        .update(rest as never)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['paiements_formateurs'] });
    },
  });
}

// =====================================================================
// BULK MARQUER PAYÉS — marquer plusieurs lignes en une fois (1 modal global)
// =====================================================================

export interface BulkMarquerPayeInput {
  paiements: PaiementFormateurRow[];
  dateReglement: string;
  modePaiement: DossierPaiementMode;
  caisseId: string | null;
  compteBancaireId: string | null;
}

export interface BulkMarquerPayeResult {
  success: number;
  errors: { paiement_id: string; message: string }[];
}

export function useBulkMarquerPayeFormateurs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkMarquerPayeInput): Promise<BulkMarquerPayeResult> => {
      const result: BulkMarquerPayeResult = { success: 0, errors: [] };
      let canal: 'especes' | 'ccp' | 'banque';
      if (input.modePaiement === 'especes') canal = 'especes';
      else if (input.modePaiement === 'ccp') canal = 'ccp';
      else canal = 'banque';
      if (canal === 'especes' && !input.caisseId) {
        throw new Error('Caisse obligatoire pour un paiement en espèces');
      }
      if (canal !== 'especes' && !input.compteBancaireId) {
        throw new Error('Compte bancaire obligatoire pour ce mode de paiement');
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      for (const paiement of input.paiements) {
        if (paiement.statut === 'paye') {
          // Skip silencieux : déjà payé.
          continue;
        }
        try {
          const ref = `FORM-${paiement.annee}-${Math.floor(Math.random() * 9000 + 1000)}`;
          const motif = paiement.formation_titre
            ? `Honoraires formateur — ${paiement.formation_titre}`
            : `Honoraires formateur — ${paiement.formateur_nom}`;
          const { data: opCreated, error: errOp } = await supabase
            .from('operations')
            .insert({
              entreprise_id: paiement.entreprise_id,
              reference: ref,
              date_operation: `${input.dateReglement}T12:00:00`,
              sens: 'sortie',
              canal,
              categorie: 'sous_traitance',
              caisse_id: canal === 'especes' ? input.caisseId : null,
              compte_bancaire_id: canal !== 'especes' ? input.compteBancaireId : null,
              tier_id: paiement.formateur_tier_id,
              activite_id: paiement.activite_id,
              activite_beneficiaire_id: paiement.activite_id,
              activite_emettrice_id: paiement.activite_id,
              devise_code: 'DZD',
              montant: paiement.montant,
              taux_change: 1,
              montant_dzd: paiement.montant,
              motif,
              observation: `Bulk paye — paiement ${paiement.id.slice(0, 8)}`,
              statut: 'validee',
              created_by: createdBy,
            } as never)
            .select('id')
            .single();
          if (errOp) {
            result.errors.push({ paiement_id: paiement.id, message: errOp.message });
            continue;
          }
          const operationId = (opCreated as { id: string } | null)?.id;
          if (!operationId) {
            result.errors.push({ paiement_id: paiement.id, message: 'Op créée sans id' });
            continue;
          }
          const { error: errUpd } = await supabase
            .from('paiements_formateurs')
            .update({
              statut: 'paye',
              date_reglement: input.dateReglement,
              mode_paiement: input.modePaiement,
              reference_operation: ref,
              operation_id: operationId,
            } as never)
            .eq('id', paiement.id);
          if (errUpd) {
            result.errors.push({ paiement_id: paiement.id, message: errUpd.message });
            continue;
          }
          result.success++;
        } catch (e) {
          result.errors.push({ paiement_id: paiement.id, message: (e as Error).message });
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['paiements_formateurs'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}
